/**
 * LHDataTableNoPagination
 *
 * A thin wrapper around the shared LHDataTable that hides the built-in
 * pagination controls on web. On native platforms it renders LHDataTable as-is.
 *
 * How it works (web only):
 *   1. Wraps LHDataTable in a View with a ref.
 *   2. After mount (and on re-renders) locates the pagination container by
 *      searching for `data-testid="rows-per-page-dropdown"` — an attribute
 *      already present inside LHDataTable — then walks up the DOM tree to
 *      find the pagination root (the sibling of the horizontal ScrollView)
 *      and sets `display: none` on it.
 *   3. A MutationObserver re-runs the logic whenever React re-renders the
 *      subtree so it stays hidden after data changes.
 */

import React, { useRef, useEffect } from 'react';
import { View, Platform } from 'react-native';
import LHDataTable from '../../../../../components/LHDataTable';

type LHDataTableProps = React.ComponentProps<typeof LHDataTable>;

const LHDataTableNoPagination: React.FC<LHDataTableProps> = (props) => {
  const wrapperRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const wrapper = wrapperRef.current as unknown as HTMLElement | null;
    if (!wrapper) return;

    const hidePagination = () => {
      // Locate the pagination area via a known testID inside LHDataTable
      const marker = wrapper.querySelector(
        '[data-testid="rows-per-page-dropdown"]',
      ) ?? wrapper.querySelector('[data-testid="first-page-button"]');

      if (!marker) return;

      // Walk up from the marker until we find an element whose parent also
      // contains a scrollable sibling (the horizontal ScrollView). That
      // element is the pagination container.
      let current: HTMLElement | null = marker as HTMLElement;
      while (current && current !== wrapper) {
        const parentNode: HTMLElement | null = current.parentElement;
        if (!parentNode) break;

        const siblings = Array.from(parentNode.children) as HTMLElement[];
        const ref = current;
        const hasScrollSibling = siblings.some((sib) => {
          if (sib === ref) return false;
          const cs = window.getComputedStyle(sib);
          return cs.overflowX === 'auto' || cs.overflowX === 'scroll';
        });

        if (hasScrollSibling) {
          current.style.display = 'none';
          return;
        }
        current = parentNode;
      }
    };

    // Run after the browser paints
    requestAnimationFrame(hidePagination);

    // Re-run whenever the subtree changes (e.g. new data, sort toggle)
    const observer = new MutationObserver(() =>
      requestAnimationFrame(hidePagination),
    );
    observer.observe(wrapper, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [props.data]);

  return (
    <View ref={wrapperRef} style={{ flex: 1 }}>
      <LHDataTable {...props} />
    </View>
  );
};

export default LHDataTableNoPagination;
